import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Avatar, Button, Tag, Spin, Modal, Tooltip, message } from "antd";
import axios from "axios";
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  DownloadOutlined,
  EditOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  FilePdfOutlined,
  EyeOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import { TalentDetailsWrapper } from "./TalentDetails.style";
import { API_CONST } from "../../../const";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return "-";
  const numValue = Number(amount);
  if (isNaN(numValue)) return "-";
  return `₹ ${numValue.toLocaleString('en-IN')}`;
};

const getFileNameFromUrl = (url) => {
  if (!url) return null;
  const parts = url.split("/");
  return parts[parts.length - 1] || null;
};

const TalentDetails = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const [downloadingBranded, setDownloadingBranded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [thumbnailErrors, setThumbnailErrors] = useState({});
  const [loadingDoc, setLoadingDoc] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const backPath = location.state?.from || "/partner/bench-pool";
  const backLabel = location.state?.fromLabel || "Back to Bench Pool";

  useEffect(() => {
    const fetchTalentProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_TALENT_PROFILE}/${talentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.profile) {
          const profile = response.data.profile;
          const partnerRate = profile.partnerRate || profile.rate || profile.monthlyRate || 0;
          const settledCost = profile.settledCost || 0;
          setTalent({
            ...profile,
            location: profile.location,
            experience: `${profile.experienceYears || 0} Years${profile.experienceMonths > 0 ? ` ${profile.experienceMonths} Months` : ''}`,
            noticePeriod: profile.notice || profile.noticePeriod || "Not specified",
            monthlyRate: formatCurrency(partnerRate),
            settledEarning: formatCurrency(settledCost),
            organization: profile.partnerOrganization,
            phone: profile.phone,
            skills: profile.skills || [],
            primarySkills: profile.primarySkills || [],
            secondarySkills: profile.secondarySkills || [],
            projects: profile.projects || [],
            resumeUrl: profile.resumeUrl,
            aadhaarUrl: profile.aadhaarUrl,
            panUrl: profile.panUrl,
            degreeUrl: profile.degreeUrl,
            aadhaarNumber: profile.aadhaarNumber,
            panNumber: profile.panNumber,
          });

          const docs = [
            {
              type: "resume",
              typeName: "Resume",
              url: profile.resumeUrl || null,
              fileName: getFileNameFromUrl(profile.resumeUrl),
            },
            {
              type: "aadhaar",
              typeName: "Aadhaar Card",
              url: profile.aadhaarUrl || null,
              fileName: getFileNameFromUrl(profile.aadhaarUrl),
              idNumber: profile.aadhaarNumber || null,
              idLabel: "Aadhaar Number",
            },
            {
              type: "pan",
              typeName: "PAN Card",
              url: profile.panUrl || null,
              fileName: getFileNameFromUrl(profile.panUrl),
              idNumber: profile.panNumber || null,
              idLabel: "PAN Number",
            },
            {
              type: "degree",
              typeName: "Degree Certificate",
              url: profile.degreeUrl || null,
              fileName: getFileNameFromUrl(profile.degreeUrl),
            },
          ];
          setDocuments(docs);
        }
      } catch (error) {
        console.error("Error fetching talent profile:", error);
        message.error("Failed to load talent profile");
      } finally {
        setLoading(false);
      }
    };

    if (talentId) {
      fetchTalentProfile();
    }
  }, [talentId]);

  useEffect(() => {
    if (!documents.some((d) => d.url)) return;
    const loadThumbnails = async () => {
      const token = localStorage.getItem("authToken");
      const urls = {};
      for (const doc of documents) {
        if (doc.url) {
          try {
            const response = await axios.post(
              API_CONST.GET_DOCUMENT_URL,
              { objectPath: doc.url },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            urls[doc.type] = response.data.downloadUrl;
          } catch (e) {
            // skip silently
          }
        }
      }
      setThumbnailUrls(urls);
    };
    loadThumbnails();
  }, [documents]);

  const getDocumentDownloadUrl = async (objectPath) => {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(
      API_CONST.GET_DOCUMENT_URL,
      { objectPath },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.downloadUrl;
  };

  const handleView = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      setPreviewUrl(downloadUrl);
      setPreviewTitle(doc.typeName);
      setPreviewVisible(true);
    } catch {
      message.error("Failed to load document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDocDownload = async (doc) => {
    if (!doc.url) { message.warning("No document uploaded"); return; }
    setLoadingDoc(doc.type);
    try {
      const downloadUrl = await getDocumentDownloadUrl(doc.url);
      window.open(downloadUrl, "_blank");
    } catch {
      message.error("Failed to download document");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDownloadResume = async () => {
    if (!talent?.resumeUrl) {
      message.warning("No resume available for download");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: talent.resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error("Error downloading resume:", error);
      message.error("Failed to download resume");
    }
  };

  const handleEdit = () => {
    navigate(`/partner/add-talent?edit=${talentId}`);
  };

  const availableDocs = documents.filter((d) => d.url);

  if (loading) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "100px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
          <p style={{ marginTop: 16, color: "#666" }}>Loading talent profile...</p>
        </div>
      </TalentDetailsWrapper>
    );
  }

  if (!talent) {
    return (
      <TalentDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Talent not found</h2>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </TalentDetailsWrapper>
    );
  }

  return (
    <TalentDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
      </div>

      <Card className="profile-card">
        <div className="profile-header">
          <div className="avatar-section">
            <Avatar size={100} className="talent-avatar">
              {talent.name?.charAt(0) || '?'}
            </Avatar>
          </div>

          <div className="info-section">
            <h1>{talent.name}</h1>
            <p className="role">{talent.role}</p>

            <div className="verification-badges">
              {talent.idVerificationStatus === "Verified" && (
                <Tag
                  icon={<CheckCircleFilled />}
                  color="success"
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  ID Verified
                </Tag>
              )}
              {talent.idVerificationStatus === "Failed" && (
                <Tag
                  color="error"
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  ID Failed
                </Tag>
              )}
              {talent.idVerificationStatus === "Pending" && (
                <Tag
                  color="warning"
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  ID Verification Pending
                </Tag>
              )}
              {talent.backgroundVerified && (
                <Tag
                  icon={talent.backgroundVerified === "Yes" ? <CheckCircleFilled /> : null}
                  color={talent.backgroundVerified === "Yes" ? "success" : talent.backgroundVerified === "Pending" ? "warning" : "default"}
                  style={{ fontSize: "13px", padding: "4px 12px", borderRadius: "4px" }}
                >
                  {talent.backgroundVerified === "Yes" ? "Background Verified" :
                   talent.backgroundVerified === "Pending" ? "BG Verification Pending" : "BG Not Verified"}
                </Tag>
              )}
              <Tag color={talent.status === "Active" ? "green" : "red"}>
                {talent.status}
              </Tag>
            </div>

            <div className="meta-info">
              <div className="info-item">
                <EnvironmentOutlined />
                <span>{talent.location}</span>
              </div>
              <div className="info-item">
                <ClockCircleOutlined />
                <span>{talent.experience} Experience</span>
              </div>
              <div className="info-item">
                <CalendarOutlined />
                <span>{talent.noticePeriod} Notice</span>
              </div>
            </div>
          </div>

          <div className="action-section">
            <Button type="primary" icon={<EditOutlined />} block onClick={handleEdit}>
              Edit Profile
            </Button>
            <Button
              icon={<DownloadOutlined />}
              block
              loading={downloadingBranded}
              onClick={async () => {
                setDownloadingBranded(true);
                try {
                  const token = localStorage.getItem("authToken");
                  const response = await axios.get(
                    `${API_CONST.BRANDED_RESUME_BASE}/${talentId}/branded-resume`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (response.data.downloadUrl) window.open(response.data.downloadUrl, "_blank");
                  else message.error("Could not get resume download URL");
                } catch (e) {
                  message.error("Failed to download HireNXT resume");
                } finally {
                  setDownloadingBranded(false);
                }
              }}
            >
              Download HireNXT Resume
            </Button>
            <Button
              icon={<DownloadOutlined />}
              block
              loading={downloadingOriginal}
              disabled={!talent?.resumeUrl}
              onClick={handleDownloadResume}
            >
              Download Original Resume
            </Button>
          </div>
        </div>

        <div className="details-row">
          <div className="detail-item">
            <h4>Email Address</h4>
            <p>{talent.email}</p>
          </div>
          <div className="detail-item">
            <h4>Phone Number</h4>
            <p>{talent.phone}</p>
          </div>
          <div className="detail-item">
            <h4>Monthly Rate</h4>
            <p className="highlight">{talent.monthlyRate}</p>
          </div>
          <div className="detail-item">
            <h4>Settled Earning</h4>
            <p className="highlight">{talent.settledEarning}</p>
          </div>
          <div className="detail-item">
            <h4>Availability</h4>
            <p>{talent.availability || "Not specified"}</p>
          </div>
          <div className="detail-item">
            <h4>Organization</h4>
            <p>{talent.organization}</p>
          </div>
        </div>
      </Card>

      <Card className="summary-card">
        <h3>Professional Summary</h3>
        <p>{talent.summary || "No summary available"}</p>
      </Card>

      <Card className="skills-card">
        <h3>Primary Skills</h3>
        <div className="skills-grid">
          {talent.primarySkills && talent.primarySkills.length > 0 ? (
            talent.primarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="cyan" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))
          ) : (
            <p style={{ color: "#999" }}>No primary skills added</p>
          )}
        </div>
      </Card>

      {talent.secondarySkills && talent.secondarySkills.length > 0 && (
        <Card className="skills-card">
          <h3>Secondary Skills</h3>
          <div className="skills-grid">
            {talent.secondarySkills.filter(Boolean).map((skill, index) => (
              <Tag key={index} color="blue" style={{ fontSize: 14, padding: "6px 12px" }}>
                {skill}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {talent.projects && talent.projects.length > 0 && (
        <Card className="projects-card">
          <h3>Project Experience</h3>
          {talent.projects.map((project, index) => (
            <div key={index} className="project-item">
              <h4>{project.title}</h4>
              <div className="project-meta">
                {project.client && <span><TeamOutlined /> {project.client}</span>}
                {project.duration && <span><CalendarOutlined /> {project.duration}</span>}
                {project.role && <span><DollarOutlined /> {project.role}</span>}
              </div>
              {project.description && <p>{project.description}</p>}
              {project.technologies && (
                <div className="tech-stack">
                  {(Array.isArray(project.technologies)
                    ? project.technologies
                    : project.technologies.split(',').map(t => t.trim()).filter(Boolean)
                  ).map((tech, idx) => (
                    <Tag key={idx} color="blue">
                      {tech}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {availableDocs.length > 0 && (
        <Card
          style={{
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            marginTop: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <FileProtectOutlined style={{ fontSize: 20, color: "#014c75" }} />
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#333" }}>
              Talent Documents
            </h3>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {availableDocs.map((doc, idx) => (
              <div
                key={idx}
                style={{
                  background: "#f8f9fd",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  style={{
                    background: "white",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e8e8e8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FilePdfOutlined style={{ fontSize: 18, color: "#014c75" }} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>{doc.typeName}</span>
                  </div>
                  <Tag icon={<CheckCircleFilled />} color="success" style={{ margin: 0 }}>
                    Uploaded
                  </Tag>
                </div>

                <div style={{ padding: 16 }}>
                  <div
                    style={{
                      background: "#fff",
                      border: "2px dashed #e8e8e8",
                      borderRadius: 8,
                      height: 140,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 14,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      overflow: "hidden",
                    }}
                    onClick={() => handleView(doc)}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#014c75"; e.currentTarget.style.background = "#f0f5ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.background = "#fff"; }}
                  >
                    {loadingDoc === doc.type ? (
                      <LoadingOutlined style={{ fontSize: 36, color: "#014c75" }} spin />
                    ) : thumbnailUrls[doc.type] && !thumbnailErrors[doc.type] ? (
                      <img
                        src={thumbnailUrls[doc.type]}
                        alt={doc.typeName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={() => setThumbnailErrors((prev) => ({ ...prev, [doc.type]: true }))}
                      />
                    ) : thumbnailUrls[doc.type] && thumbnailErrors[doc.type] ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <FilePdfOutlined style={{ fontSize: 36, color: "#ff4d4f", marginBottom: 8 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#ff4d4f", letterSpacing: 1 }}>PDF</span>
                        <span style={{ fontSize: 12, color: "#999", marginTop: 4 }}>Click to preview</span>
                      </div>
                    ) : (
                      <>
                        <FilePdfOutlined style={{ fontSize: 36, color: "#bbb", marginBottom: 8 }} />
                        <span style={{ fontSize: 12, color: "#999" }}>Click to preview</span>
                      </>
                    )}
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderBottom: doc.idLabel ? "1px solid #f0f0f0" : "none",
                      }}
                    >
                      <span style={{ color: "#999", fontSize: 12 }}>File</span>
                      <span
                        style={{ color: "#333", fontSize: 12, fontWeight: 500, maxWidth: 160, textAlign: "right", wordBreak: "break-all" }}
                        title={doc.fileName}
                      >
                        {doc.fileName
                          ? doc.fileName.length > 22
                            ? doc.fileName.substring(0, 22) + "..."
                            : doc.fileName
                          : "-"}
                      </span>
                    </div>
                    {doc.idLabel && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "6px 0",
                        }}
                      >
                        <span style={{ color: "#999", fontSize: 12 }}>{doc.idLabel}</span>
                        <Tooltip title={!doc.idNumber ? "Number not yet extracted" : ""}>
                          <span
                            style={{
                              color: doc.idNumber ? "#014c75" : "#ccc",
                              fontSize: 12,
                              fontWeight: doc.idNumber ? 600 : 400,
                              fontFamily: doc.idNumber ? "monospace" : "inherit",
                              letterSpacing: doc.idNumber ? "0.5px" : "normal",
                            }}
                          >
                            {doc.idNumber || "Not extracted"}
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(doc)}
                      loading={loadingDoc === doc.type}
                      style={{ flex: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDocDownload(doc)}
                      loading={loadingDoc === doc.type}
                      style={{ flex: 1, background: "#00d9a9", borderColor: "#00d9a9" }}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={900}
        centered
      >
        <iframe
          src={previewUrl}
          style={{ width: "100%", height: "70vh", border: "none" }}
          title={previewTitle}
        />
      </Modal>
    </TalentDetailsWrapper>
  );
};

export default TalentDetails;
